import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';
import { gqlMediaTypeCodeEnum as MEDIA_TYPE, gqlProfile } from '@minecraft.graphql-types';
import CloudinaryMediaViewer from '@minecraft.cloudinary-media-viewer';
import { AppType } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { AnchorButton } from '../styles/ProfileView';
import creditSortOrder from '../shared/utils/creditSortOrder';

import {
  StyledRole,
  StyledIcon,
  InfoRowItems,
  StyledButton,
  ButtonContainer,
  InfoRow,
  StyledUserInfo,
  CommaSpan,
  InfoHead,
} from './Styles';

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

type Props = {
  isPreview?: boolean;
  profile: gqlProfile;
  'data-qa-id': string;
  isAdmin?: boolean;
  onEditClick: (value) => void;
  appType: AppType;
};

const CreditMedia = ({
  profile,
  'data-qa-id': qaId,
  isPreview = false,
  isAdmin,
  onEditClick,
  appType,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [isCarouselOpen, setIsCarouselOpen] = React.useState(false);
  const [creditMedia, setCreditMedia] = React.useState(null);
  const profileId = profile && Number(profile.profileId);
  const handleMediaClick = (selectedMedia): void => {
    if (selectedMedia.isLocked && appType === AppType.Talent) return;

    setIsCarouselOpen(true);
    setCreditMedia(selectedMedia);
  };

  const handleCloseModal = (): void => {
    setIsCarouselOpen(false);
  };

  const creditMediaSorted = profile?.credits && creditSortOrder(profile.credits.filter((c) => c.highlighted));

  const renderInfoRowItems = (credits, qaID: string): JSX.Element[] => {
    return credits.map((credit, index) => (
      <Fragment key={credit.id}>
        <StyledRole data-qa-id={`${qaID}-title`}>
          {credit.title} {!profile?.hideCreditYear && credit.year}
          {credit.creditMedia &&
            !_isEmpty(credit.creditMedia) &&
            credit.creditMedia[0].mediaType.code === MEDIA_TYPE.VIDEO && (
              <StyledIcon
                data-qa-id={`${qaId}-media-play-icon`}
                name="media-play-circle"
                onClick={(): void => handleMediaClick({ media: credit.creditMedia[0] })}
              />
            )}
        </StyledRole>
        {+index + 1 < +creditMediaSorted.length && <CommaSpan>,</CommaSpan>}
      </Fragment>
    ));
  };

  const creditButton = isAdmin ? (
    <AnchorButton data-qa-id={`${qaId}-edit`} onClick={(): void => onEditClick('credits')}>
      {t('common:button.edit')}
    </AnchorButton>
  ) : (
    <DuoLink data-qa-id={`${qaId}-edit`} href={`/profile/${profileId}/credits`}>
      {t('common:button.edit')}
    </DuoLink>
  );
  const commonButton = isAdmin ? (
    <StyledButton data-qa-id={`${qaId}-add`} onClick={(): void => onEditClick('credits')} outlined="true">
      {t('common:profile.button.addHighlightedCredits')}
    </StyledButton>
  ) : (
    <DuoLink data-qa-id={`${qaId}-add`} href={`/profile/${profileId}/credits`}>
      <StyledButton outlined="true">{t('common:profile.button.addHighlightedCredits')}</StyledButton>
    </DuoLink>
  );

  return (
    <>
      {' '}
      {creditMediaSorted && creditMediaSorted.length ? (
        <StyledUserInfo data-testid="highlighted-credits">
          <InfoRow>
            <InfoHead>{t('common:profile.info.highlightedCredits')}</InfoHead>
            {!isPreview && creditButton}
          </InfoRow>
          <InfoRowItems data-testid="credits-text">{renderInfoRowItems(creditMediaSorted, qaId)}</InfoRowItems>
        </StyledUserInfo>
      ) : (
        !isPreview && <ButtonContainer>{commonButton}</ButtonContainer>
      )}
      {isCarouselOpen && (
        <CloudinaryMediaViewer isSmall stageName="" mediaList={[creditMedia]} handleCloseModal={handleCloseModal} />
      )}
    </>
  );
};

export default CreditMedia;
